import { OverlayModule } from '@angular/cdk/overlay'
import * as ngCore from '@angular/core'
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser'

import * as color from '../../../../color'
import * as button from '../../../button'
import * as textInput from '../../../input/text' // TODO: if we import from 'input' it causes circular dependency issues.
import * as centeredOverlay from '../centered'

/**
 * A specialized type of "centered overlay" component that displays a confirmation dialog.
 * It internally uses the {@link centeredOverlay} component.
 * You can customize the dialog by providing different inputs.
 *
 * #### Inputs
 * - {@link Component.title|title}?
 * - {@link Component.validation|validation}?
 * - {@link Component.description|description}?
 * - {@link Component.error|error}?
 * - {@link Component.confirmButtonStyle|confirmButtonStyle}?
 * - {@link Component.cancelButtonStyle|cancelButtonStyle}?
 * - {@link Component.confirmButtonText|confirmButtonText}?
 * - {@link Component.cancelButtonText|cancelButtonText}?
 *
 * Elements marked with `details` are displayed below the dialog description.
 *
 * #### Outputs
 * - {@link Component.confirm|confirm}
 * - {@link Component.cancel|cancel}
 *
 * @example
 * ```html
 * <atn-ds-confirmation-overlay></atn-ds-confirmation-overlay>
 * <!-- or as self-closing tag -->
 * <atn-ds-confirmation-overlay />
 * ```
 */
@ngCore.Component({
  imports: [
    OverlayModule,
    button.Component,
    centeredOverlay.Component,
    textInput.Component,
  ],
  selector: 'atn-ds-confirmation-overlay',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link DomSanitizer} */
  private readonly _sanitizer = ngCore.inject(DomSanitizer)
  /**
   * Event emitted when the confirm button is clicked.
   */
  public readonly confirm = ngCore.output()
  /**
   * Event emitted when the cancel button is clicked.
   * This event is also emitted when the backdrop is clicked.
   */
  public readonly cancel = ngCore.output()

  /**
   * Dialog title
   */
  public readonly titleSg = ngCore.input<string>('Are you sure?', {
    alias: 'title',
  })

  /**
   * Indicates if async operation is in progress during confirmation (e.g.,
   * waiting for server response when action is being confirmed)
   */
  public readonly loadingSg = ngCore.input(false, { alias: 'loading' })

  /**
   * Dialog description
   */
  public readonly descriptionSg = ngCore.input<SafeHtml, string>(
    'Are you sure you want to proceed?',
    {
      alias: 'description',
      transform: (value: string) =>
        this._sanitizer.bypassSecurityTrustHtml(value),
    },
  )

  /**
   * Optional user-facing error message for failed confirmation mutations.
   */
  public readonly errorSg = ngCore.input<string | undefined>(undefined, {
    alias: 'error',
  })

  /**
   * Optional validation text required for delete confirmation.
   *
   * If this input is provided, the delete confirmation dialog will include a
   * text input field where the user must type the exact value of `validation`
   * in order to proceed with deletion. This is typically used as an extra
   * safeguard to prevent accidental deletions.
   *
   * If not provided, a simple confirmation dialog will be shown with a single
   * "Confirm" button and no input field.
   */
  public readonly validationTextSg = ngCore.input<string>(undefined, {
    alias: 'validation',
  })

  /**
   * Confirm button style
   */
  public readonly confirmButtonStyleSg =
    ngCore.input<color.Legacy.PredefinedStyle>(
      color.Legacy.PredefinedStyle.Danger,
      {
        alias: 'confirmButtonStyle',
      },
    )

  /**
   * Cancel button style
   */
  public readonly cancelButtonStyleSg =
    ngCore.input<color.Legacy.PredefinedStyle>(
      color.Legacy.PredefinedStyle.Transparent,
      {
        alias: 'cancelButtonStyle',
      },
    )

  /**
   * Confirm button text
   */
  public readonly confirmButtonTextSg = ngCore.input<string>('Yes', {
    alias: 'confirmButtonText',
  })

  /**
   * Cancel button text
   */
  public cancelButtonTextSg = ngCore.input<string>('Cancel', {
    alias: 'cancelButtonText',
  })

  /** Value of user's validation input for delete confirmation */
  public readonly validationInputSg = ngCore.signal<string | undefined>(
    undefined,
  )

  /**
   * Determines whether the confirmation should be triggered.
   * Confirmation is possible in the following two cases:
   * 1. When the validation text is not provided.
   * 2. When the validation text is provided and it matches the user's input.
   */
  public readonly canConfirmSg = ngCore.computed(
    (validationText = this.validationTextSg()) =>
      validationText === undefined ||
      validationText === this.validationInputSg(),
  )

  /** Confirm button click handler*/
  public confirmClickHandler(): void {
    if (this.canConfirmSg()) {
      this.confirm.emit()
    }
  }
}
